/**
 * Equipment Engine — computes how gear modifies launch conditions.
 *
 * Two layers:
 *   1. Legacy modifiers (ballSpeedMod, launchMod, spinMod) — simple multipliers
 *      from the old model. Still apply as the base layer.
 *   2. DNA modifiers — physics-based adjustments from gear internals:
 *      - Shaft profile → kick point, torque, tip stiffness affect launch/spin/dispersion
 *      - Head profile → CG position affects spin/launch, MOI affects mishit tolerance
 *      - Ball profile → layer count affects spin separation, dimples affect flight shape
 *      - Swing-shaft interaction → tempo × butt stiffness, speed × torque
 *
 * The DNA layer adds small deltas on top of the legacy modifiers. At default
 * DNA values (kick=mid, torque=3.5, tip=1.0, etc.), the DNA layer is neutral —
 * backward compatible with the old model.
 */

import type {
  EquipmentBag, LaunchModifiers, GearSpecs, ShaftFlex, IronCategory,
  ShaftProfile, HeadProfile, BallProfile, GolfBall,
} from './types';
import type { ClubPreset, Tempo, Transition } from '../types';
import { computeShaftLoading } from './ShaftLoadingModel';
import { computeCORSpeedMod, computeCoverSpinMod, computeDeformationLaunchDelta } from './BallPhysicsModel';

export interface DeliveryContext {
  attackAngle?: number;
}

// ── Shaft flex lookup tables ──────────────────────────────────────────────

const FLEX_LAUNCH: Record<ShaftFlex, number> = { L: 2.0, A: 1.5, R: 1.0, S: 0, X: -0.8 };
const FLEX_SPIN:   Record<ShaftFlex, number> = { L: 1.05, A: 1.03, R: 1.02, S: 1.0, X: 0.97 };
const FLEX_SPEED:  Record<ShaftFlex, number> = { L: 1.01, A: 1.005, R: 1.0, S: 1.0, X: 0.995 };

// ── Iron category lookup tables ──────────────────────────────────────────

const IRON_CAT_SPEED:  Record<IronCategory, number> = { blade: 0.97, players: 1.0, 'players-distance': 1.03, 'game-improvement': 1.04 };
const IRON_CAT_LAUNCH: Record<IronCategory, number> = { blade: -1.0, players: 0, 'players-distance': 0.5, 'game-improvement': 1.5 };
const IRON_CAT_SPIN:   Record<IronCategory, number> = { blade: 1.10, players: 1.0, 'players-distance': 0.92, 'game-improvement': 0.88 };

// ── DNA Physics Functions ─────────────────────────────────────────────────

/**
 * Compute shaft DNA modifiers using the 3-segment lumped mass ODE model.
 *
 * The ODE simulates how the shaft actually bends during the downswing:
 *   - Transition: shaft loads backward, butt section absorbs energy
 *   - Downswing: energy cascades tip-ward through EI profile
 *   - Impact: shaft "kicks" forward, adding dynamic loft + speed
 *
 * The model derives an EI profile from the ShaftProfile DNA, then runs
 * a 72-step RK4 integration to compute tip deflection, kick timing,
 * shaft lean, and face closure rate at impact.
 *
 * Length effects are applied on top (not part of the ODE — it's a
 * geometric lever arm effect, not a bending dynamic).
 */
function computeShaftDNA(
  profile: ShaftProfile,
  shaft: { weight: number; length: number; material: 'graphite' | 'steel' },
  clubSpeedMph: number,
  tempo: Tempo = 'medium',
  transition: Transition = 'medium',
  headMassKg: number = 0.200,
): { launchDelta: number; spinMult: number; speedMult: number; dispersionMult: number } {
  // Run the shaft loading ODE simulation
  const loading = computeShaftLoading(
    profile, shaft.weight, shaft.material, shaft.length,
    clubSpeedMph, tempo, transition, headMassKg,
  );

  // Length effect: +0.5" over 45.5" standard = ~1.5 mph more speed, wider dispersion
  // This is a geometric lever arm effect, separate from shaft bending dynamics
  const lengthDelta = shaft.length - 45.5;
  const lengthSpeedMult = 1.0 + lengthDelta * 0.007;       // ~0.7% per half inch
  const lengthDispMult = 1.0 + Math.abs(lengthDelta) * 0.06; // 6% wider per half inch longer

  return {
    launchDelta: loading.launchDelta,
    spinMult: loading.spinMult,
    speedMult: loading.speedMult * lengthSpeedMult,
    dispersionMult: loading.dispersionMult * lengthDispMult,
  };
}

/**
 * Compute head DNA modifiers.
 *
 * Physics model:
 *   - CG depth: deeper CG = more spin (gear effect), higher launch (dynamic loft)
 *   - CG height: lower CG = higher launch for same loft
 *   - MOI: doesn't affect center hits — only mishit tolerance (modeled in variance)
 *   - Face flex: directly scales ball speed (higher CT = more energy transfer)
 *
 * Baseline (neutral DNA): cgDepth=28, cgHeight=20 for drivers.
 */
function computeHeadDNA(
  head: HeadProfile,
  isDriver: boolean,
): { launchDelta: number; spinMult: number; speedMult: number; moiDispMult: number } {
  const refDepth = isDriver ? 28 : 6;
  const refHeight = isDriver ? 20 : 15;
  const refMoi = isDriver ? 5000 : 2800;

  // CG depth: deeper = more spin, higher launch
  // Driver: ~50 rpm per mm of CG depth change
  // Iron: ~30 rpm per mm (smaller effect due to already high spin)
  const depthDelta = head.cgDepth - refDepth;
  const spinPerMm = isDriver ? 0.0065 : 0.003;  // as spin multiplier per mm
  const launchPerMm = isDriver ? 0.04 : 0.03;  // degrees per mm

  const depthSpinMult = 1.0 + depthDelta * spinPerMm;
  const depthLaunchDelta = depthDelta * launchPerMm;

  // CG height: lower = higher launch
  // ~0.08° per mm for drivers, ~0.05° for irons
  const heightDelta = refHeight - head.cgHeight;  // positive = lower CG = higher launch
  const heightLaunchPerMm = isDriver ? 0.08 : 0.05;
  const heightLaunchDelta = heightDelta * heightLaunchPerMm;

  // Face flex → ball speed
  const speedMult = head.faceFlexMod;

  // MOI → dispersion reduction on mishits
  // At reference MOI (5000), dispersion mult = 1.0
  // Higher MOI reduces dispersion: 5900 → 0.82×, 4000 → 1.18×
  const moiDelta = head.moi - refMoi;
  const moiDispMult = 1.0 - moiDelta * 0.0002;  // ±0.2% per g·cm²

  return {
    launchDelta: depthLaunchDelta + heightLaunchDelta,
    spinMult: depthSpinMult,
    speedMult,
    moiDispMult: Math.max(0.7, Math.min(1.3, moiDispMult)),
  };
}

function computeDriverSpinResponse(
  driver: EquipmentBag['driver'],
  clubSpeed: number,
  delivery?: DeliveryContext,
): number {
  const response = driver.spinResponse;
  if (!response) return 1.0;
  if (delivery?.attackAngle == null) return 1.0;

  const attackAngle = delivery.attackAngle;
  const adjustmentRpm =
    response.speedSlopeRpmPerMph * (clubSpeed - response.referenceClubSpeed) +
    response.attackSlopeRpmPerDeg * (attackAngle - response.referenceAttackAngle);
  const boundedRpm = Math.max(
    -response.maxAdjustmentRpm,
    Math.min(response.maxAdjustmentRpm, adjustmentRpm),
  );

  return 1.0 + boundedRpm / 3000;
}

/**
 * Compute ball DNA modifiers.
 *
 * Physics model:
 *   - Layer count: more layers = more spin separation between driver and wedge.
 *     2-piece: spin multiplier applies uniformly.
 *     5-piece: driver spin drops further, wedge spin stays/increases.
 *   - Dimple profile: penetrating = -0.3° launch, +0.01 Cd (lower flight).
 *     High-flight = +0.3° launch, -0.01 Cd (higher flight, steeper landing).
 */
function computeBallDNA(
  ball: BallProfile,
  isDriverWood: boolean,
  isWedge: boolean,
): { launchDelta: number; spinMult: number } {
  // Layer spin separation
  // 2-piece: 1.0 uniform. 3-piece: slight separation. 5-piece: max separation.
  // Separation = driver spin goes down, wedge spin goes up relative to baseline
  const separationFactor = (ball.layers - 3) / 4;  // -0.25 (2pc) to +0.5 (5pc)
  let spinMult = 1.0;
  if (isDriverWood) {
    spinMult = 1.0 - separationFactor * 0.03;  // 5pc: -1.5% driver spin
  } else if (isWedge) {
    spinMult = 1.0 + separationFactor * 0.04;  // 5pc: +2% wedge spin
  }

  // Dimple profile → launch bias
  let launchDelta = 0;
  if (ball.dimpleProfile === 'penetrating') {
    launchDelta = -0.3;
  } else if (ball.dimpleProfile === 'high-flight') {
    launchDelta = 0.3;
  }

  return { launchDelta, spinMult };
}

// ── Public API: Named Gear (EquipmentBag) ────────────────────────────────

/**
 * Compute combined equipment modifiers for a given club using named gear (DNA-aware).
 *
 * Layers: Head DNA → Shaft DNA → Ball DNA → Legacy modifiers → Compression interaction.
 */
export function computeModifiers(
  clubKey: string,
  bag: EquipmentBag,
  clubSpeed: number,
  tempo: Tempo = 'medium',
  transition: Transition = 'medium',
  /** Skip all ball-layer effects (legacy + DNA + compression physics). */
  skipBallEffects = false,
  delivery?: DeliveryContext,
): LaunchModifiers {
  let ballSpeedMod = 1.0;
  let launchMod = 0;
  let spinMod = 1.0;
  let dispersionMod = 1.0;
  const isDriverWood = clubKey === 'driver' || clubKey === '3wood';

  // ── Legacy club head layer ──
  if (isDriverWood) {
    ballSpeedMod *= bag.driver.ballSpeedMod;
    launchMod += bag.driver.launchMod;
    spinMod *= bag.driver.spinMod;
    spinMod *= computeDriverSpinResponse(bag.driver, clubSpeed, delivery);
  } else {
    ballSpeedMod *= bag.irons.ballSpeedMod;
    launchMod += bag.irons.launchMod;
    spinMod *= bag.irons.spinMod;
  }

  // ── Head DNA layer ──
  const headProfile = isDriverWood ? bag.driver.head : bag.irons.head;
  const headDna = computeHeadDNA(headProfile, isDriverWood);
  ballSpeedMod *= headDna.speedMult;
  launchMod += headDna.launchDelta;
  spinMod *= headDna.spinMult;
  dispersionMod *= headDna.moiDispMult;

  // ── Legacy shaft layer ──
  launchMod += bag.shaft.launchMod;
  spinMod *= bag.shaft.spinMod;

  // ── Shaft DNA layer (ODE simulation) ──
  const headMassKg = isDriverWood ? 0.200 : 0.260;
  const shaftDna = computeShaftDNA(
    bag.shaft.profile,
    { weight: bag.shaft.weight, length: bag.shaft.length, material: bag.shaft.material },
    clubSpeed, tempo, transition, headMassKg,
  );
  ballSpeedMod *= shaftDna.speedMult;
  launchMod += shaftDna.launchDelta;
  spinMod *= shaftDna.spinMult;
  dispersionMod *= shaftDna.dispersionMult;

  // ── Ball layer (optional) ──
  if (!skipBallEffects) {
    const ballMods = computeBallModifiers(clubKey, bag.ball, clubSpeed);
    ballSpeedMod *= ballMods.ballSpeedMod;
    launchMod += ballMods.launchMod;
    spinMod *= ballMods.spinMod;
  }

  return { ballSpeedMod, launchMod, spinMod, dispersionMod };
}

/**
 * Compute DNA-only modifiers: the incremental physics improvement from real
 * gear components (head CG/face flex, shaft loading ODE) without legacy
 * per-model mods that overlap with spec-level choices (flex, loft, head type).
 *
 * Used in gear/ball phases to stack on top of spec modifiers:
 *   specMods (user's optimal profile) + gearDNA (component physics) + ballMods
 *
 * Returns all 1.0 for average gear, >1.0 for superior components.
 */
export function computeGearDNA(
  clubKey: string,
  bag: EquipmentBag,
  clubSpeed: number,
  tempo: Tempo = 'medium',
  transition: Transition = 'medium',
): LaunchModifiers {
  const isDriverWood = clubKey === 'driver' || clubKey === '3wood';

  let ballSpeedMod = 1.0;
  let launchMod = 0;
  let spinMod = 1.0;
  let dispersionMod = 1.0;

  // ── Head DNA: CG geometry + face flex ──
  const headProfile = isDriverWood ? bag.driver.head : bag.irons.head;
  const headDna = computeHeadDNA(headProfile, isDriverWood);
  ballSpeedMod *= headDna.speedMult;
  launchMod += headDna.launchDelta;
  spinMod *= headDna.spinMult;
  dispersionMod *= headDna.moiDispMult;

  // ── Shaft DNA: ODE loading simulation ──
  const headMassKg = isDriverWood ? 0.200 : 0.260;
  const shaftDna = computeShaftDNA(
    bag.shaft.profile,
    { weight: bag.shaft.weight, length: bag.shaft.length, material: bag.shaft.material },
    clubSpeed, tempo, transition, headMassKg,
  );
  ballSpeedMod *= shaftDna.speedMult;
  launchMod += shaftDna.launchDelta;
  spinMod *= shaftDna.spinMult;
  dispersionMod *= shaftDna.dispersionMult;

  return { ballSpeedMod, launchMod, spinMod, dispersionMod };
}

/**
 * Compute only ball-layer modifiers (legacy + DNA + compression/cover physics).
 * Used to keep ball effects explicit when stacking spec and gear layers.
 */
export function computeBallModifiers(
  clubKey: string,
  ball: GolfBall,
  clubSpeed: number,
): LaunchModifiers {
  const isDriverWood = clubKey === 'driver' || clubKey === '3wood';
  const isWedge = clubKey === 'pw' || clubKey === 'sw';

  let ballSpeedMod = 1.0;
  let launchMod = 0;
  let spinMod = 1.0;

  // ── Legacy ball layer ──
  ballSpeedMod *= ball.speedMod;
  if (isDriverWood) {
    spinMod *= ball.driverSpinMod;
  } else if (isWedge) {
    spinMod *= ball.wedgeSpinMod;
  } else {
    spinMod *= ball.ironSpinMod;
  }

  // ── Ball DNA layer ──
  const ballDna = computeBallDNA(ball.ball, isDriverWood, isWedge);
  launchMod += ballDna.launchDelta;
  spinMod *= ballDna.spinMult;

  // ── Ball compression physics (COR + deformation + cover friction) ──
  ballSpeedMod *= computeCORSpeedMod(clubSpeed, ball.compression);
  spinMod *= computeCoverSpinMod(ball.cover, clubKey);
  const estimatedBallSpeed = clubSpeed * 1.45;  // rough smash factor for deformation calc
  launchMod += computeDeformationLaunchDelta(ball.compression, estimatedBallSpeed);

  return { ballSpeedMod, launchMod, spinMod, dispersionMod: 1.0 };
}

// ── Public API: Generic Specs (no DNA) ───────────────────────────────────

/**
 * Compute equipment modifiers from individual gear specs (no DNA data).
 * Used for spec-based comparison testing when exact gear isn't known.
 */
export function computeSpecModifiers(
  specs: GearSpecs,
  clubKey: string,
  clubSpeed: number,
  /** Skip spec-ball layer (compression/cover/deformation) when ball effects come from another source. */
  skipBallPhysics = false,
  /** Imported iron comparisons use a 37-inch model reference, not a driver length. */
  referenceClubLength = 45.5,
): LaunchModifiers {
  const isDriverWood = clubKey === 'driver' || clubKey === '3wood';

  let ballSpeedMod = 1.0;
  let launchMod = 0;
  let spinMod = 1.0;
  let dispersionMod = 1.0;

  // ── Loft (driver/wood only, relative to 10.5° standard) ──
  if (isDriverWood) {
    const loftDelta = specs.loft - 10.5;
    launchMod += loftDelta * 0.7;
    spinMod *= 1 + loftDelta * 0.07;
    ballSpeedMod *= 1 - loftDelta * 0.003;
  }

  // ── Iron category (irons/wedges only) ──
  if (!isDriverWood) {
    ballSpeedMod *= IRON_CAT_SPEED[specs.ironCategory];
    launchMod += IRON_CAT_LAUNCH[specs.ironCategory];
    spinMod *= IRON_CAT_SPIN[specs.ironCategory];
  }

  // ── Shaft flex ──
  launchMod += FLEX_LAUNCH[specs.shaftFlex];
  spinMod *= FLEX_SPIN[specs.shaftFlex];
  ballSpeedMod *= FLEX_SPEED[specs.shaftFlex];

  // ── Shaft weight (relative to 65g baseline) ──
  ballSpeedMod *= 1 + (65 - specs.shaftWeight) * 0.0005;

  // ── Club length (relative to 45.5" standard) ──
  if (specs.clubLength !== undefined) {
    const lengthDelta = specs.clubLength - referenceClubLength;
    // +0.7% ball speed per 0.5" over standard
    ballSpeedMod *= 1 + lengthDelta * 0.014;
    // +5% dispersion per 0.5" over standard
    dispersionMod *= 1 + Math.max(0, lengthDelta) * 0.10;
  }

  // ── Launch profile ──
  if (specs.launchProfile !== undefined) {
    const launchProfileMap: Record<string, { launch: number; spin: number }> = {
      'low':      { launch: -0.5, spin: 0.97 },
      'mid-low':  { launch: -0.25, spin: 0.985 },
      'mid':      { launch: 0, spin: 1.0 },
      'mid-high': { launch: 0.25, spin: 1.015 },
      'high':     { launch: 0.5, spin: 1.03 },
    };
    const lp = launchProfileMap[specs.launchProfile] || launchProfileMap['mid'];
    launchMod += lp.launch;
    spinMod *= lp.spin;
  }

  // ── Head type ──
  if (specs.headType !== undefined && specs.headType !== 'standard') {
    switch (specs.headType) {
      case 'low-spin':
        spinMod *= 0.95;
        launchMod -= 0.3;
        break;
      case 'max-forgiveness':
        dispersionMod *= 0.90;
        spinMod *= 1.02;
        break;
      case 'draw':
        spinMod *= 0.98;
        break;
      case 'fade':
        spinMod *= 1.01;
        break;
    }
  }

  // ── Weight setting ──
  if (specs.weightSetting !== undefined && specs.weightSetting !== 'standard') {
    switch (specs.weightSetting) {
      case 'draw':
        dispersionMod *= 0.95;
        break;
      case 'back':
        launchMod += 0.3;
        spinMod *= 1.03;
        break;
      case 'front':
        launchMod -= 0.3;
        spinMod *= 0.97;
        break;
    }
  }

  // ── Ball compression physics (COR + deformation + cover friction) ──
  // Skip when ball effects are injected from another layer (e.g. named-ball testing).
  if (!skipBallPhysics) {
    ballSpeedMod *= computeCORSpeedMod(clubSpeed, specs.ballCompression);
    spinMod *= computeCoverSpinMod(specs.ballCover, clubKey);
    const estimatedBallSpeed = clubSpeed * 1.45;
    launchMod += computeDeformationLaunchDelta(specs.ballCompression, estimatedBallSpeed);
  }

  return { ballSpeedMod, launchMod, spinMod, dispersionMod };
}

/**
 * Apply modifiers to an amateur-scaled club preset, returning adjusted launch values.
 */
export function applyModifiers(
  preset: ClubPreset,
  mods: LaunchModifiers,
): { ballSpeed: number; launchAngle: number; spinRate: number } {
  return {
    ballSpeed: Math.round(preset.ballSpeed * mods.ballSpeedMod * 100) / 100,
    launchAngle: Math.round((preset.launchAngle + mods.launchMod) * 100) / 100,
    spinRate: Math.round(preset.spinRate * mods.spinMod),
  };
}
